import {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useWindowSize} from "react-use";
import LoaderOverlay from "../../../reusableComponents/Loader";
import {setSelectedChallenge} from "../../../store/reducers/accountSlice";
import {formatUSD} from "../../../utils/helpers/string";
import MobilePlans from "./MobilePlans";
import "./NewPlans.scss";
import mt5 from "../../../assets/icons/MT5.png";
import matchTrader from "../../../assets/icons/MatchTrader.svg";

const NewPlans = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {width} = useWindowSize();
  const isMobile = width < 768;

  const plansRedux = useSelector((state) => state?.account?.plans);
  const userCountry = useSelector((state) => state?.user?.country);

  const allPlans = useMemo(() => plansRedux?.data || [], [plansRedux?.data]);

  const baseName = (name) => {
    if (!name) return "";
    return name.replace(/^\s*(?:1|2)\s*Step\s*/i, "").trim();
  };

  // Plan Types (Legacy, Swift, Instant, Flagship)
  const planNames = useMemo(() => {
    const desiredOrder = ["Legacy", "Swift", "Instant", "Flagship"];
    const uniqueNames = Array.from(new Set(allPlans.map((p) => baseName(p?.name))));
    return uniqueNames.sort((a, b) => {
      const indexA = desiredOrder.indexOf(a);
      const indexB = desiredOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [allPlans]);

  const planBadges = {
    Legacy: "Most Popular",
    Flagship: "Highest Leverage",
    Swift: "Most Affordable",
    Instant: "Quickest to Payout",
  };

  const [selectedPlanName, setSelectedPlanName] = useState("");

  useEffect(() => {
    if (planNames.length > 0 && !selectedPlanName) {
      setSelectedPlanName(planNames[0]);
    }
  }, [planNames, selectedPlanName]);

  // Steps (1 Step, 2 Step)
  const plansForSelectedName = useMemo(() => allPlans.filter((p) => baseName(p?.name) === selectedPlanName), [allPlans, selectedPlanName]);

  const stepTypes = useMemo(() => Array.from(new Set(plansForSelectedName.map((p) => p?.type).filter(Boolean))), [plansForSelectedName]);

  const stepLabel = (type) => (type === "1_step" ? "1 Step" : type === "2_step" ? "2 Step" : type === "pro_edge" ? "Instant" : null);

  const [selectedStepType, setSelectedStepType] = useState("");

  useEffect(() => {
    if (selectedPlanName === "Instant") {
      setSelectedStepType("pro_edge");
    } else if (stepTypes.length > 0) {
      if (!selectedStepType || !stepTypes.includes(selectedStepType)) {
        setSelectedStepType(stepTypes.includes("1_step") ? "1_step" : stepTypes[0]);
      }
    } else {
      setSelectedStepType("");
    }
  }, [stepTypes, selectedPlanName, selectedStepType]);

  const currentPlan = useMemo(() => {
    if (!plansForSelectedName.length) return null;
    return plansForSelectedName.find((p) => (selectedStepType ? p?.type === selectedStepType : true)) || plansForSelectedName[0];
  }, [plansForSelectedName, selectedStepType]);

  const phases = useMemo(() => {
    if (selectedStepType === "2_step") return ["Phase 1", "Phase 2", "Funded"];
    if (selectedStepType === "1_step") return ["Phase 1", "Funded"];
    if (selectedStepType === "pro_edge") return ["Funded"];
    return [];
  }, [selectedStepType]);

  // Account Sizes (2.5K, 5K, etc.)
  const challenges = useMemo(() => [...(currentPlan?.challenges || [])].sort((a, b) => a.starting_balance - b.starting_balance), [currentPlan]);

  const [selectedChallengeId, setSelectedChallengeId] = useState(null);
  const [selectedBalance, setSelectedBalance] = useState(10000);

  // 1. Keep selectedChallengeId and selectedBalance in sync when manually changed (e.g. Swiper or click)
  useEffect(() => {
    if (selectedChallengeId && challenges.length > 0) {
      const challenge = challenges.find((c) => c.id === selectedChallengeId);
      if (challenge && challenge.starting_balance !== selectedBalance) {
        setSelectedBalance(challenge.starting_balance);
      }
    }
  }, [selectedChallengeId, challenges, selectedBalance]);

  // 2. When the challenges array changes (e.g. switching Legacy/Swift or 1/2 Step)
  useEffect(() => {
    if (challenges.length > 0) {
      // Try to find a challenge with the last known starting balance
      const matchByBalance = selectedBalance ? challenges.find((c) => c.starting_balance === selectedBalance) : null;
      
      if (matchByBalance) {
        if (selectedChallengeId !== matchByBalance.id) {
          setSelectedChallengeId(matchByBalance.id);
        }
      } else {
        // Otherwise, fall back to the first challenge in the sorted list
        setSelectedChallengeId(challenges[0].id);
        setSelectedBalance(challenges[0].starting_balance);
      }
    }
  }, [challenges]);

  const selectedChallenge = useMemo(() => challenges.find((c) => c.id === selectedChallengeId), [challenges, selectedChallengeId]);

  const tableRows = useMemo(() => {
    if (!selectedChallenge) return [];

    if (selectedStepType === "pro_edge") {
      return [
        {label: "Trading Period", value: "Unlimited"},
        {label: "Minimum Trading Days", value: selectedChallenge.min_trading_days ? `${selectedChallenge.min_trading_days} Days` : "N/A"},
        {label: "Maximum Daily Loss", value: selectedChallenge.max_daily_loss ? `${parseFloat(selectedChallenge.max_daily_loss).toFixed(0)}%` : "N/A"},
        {label: "Maximum Loss (Trailing)", value: selectedChallenge.max_loss ? `${parseFloat(selectedChallenge.max_loss).toFixed(0)}%` : "N/A"},
        {label: "Consistency Score", value: selectedChallenge.consistency_score ? `${selectedChallenge.consistency_score}%` : "N/A"},
        {label: "Platforms", isPlatforms: true},
        {label: "Max risk", value: selectedChallenge.max_risk_at_open_position ? `${parseFloat(selectedChallenge.max_risk_at_open_position).toFixed(0)}%` : "N/A"},
        {label: "Safety Cushion", value: selectedChallenge.safety_cushion_percentage ? `${parseFloat(selectedChallenge.safety_cushion_percentage).toFixed(0)}%` : "N/A"},
        {
          label: "Inactivity Rule",
          value: selectedChallenge.inactivity_days ? `${selectedChallenge.inactivity_days} Days` : "N/A",
        },
        {label: "Leverage", value: selectedChallenge.leverage ? `Up to 1:${selectedChallenge.leverage}` : "N/A"},
      ];
    }

    return [
      {label: "Trading Period", value: "Unlimited"},
      {label: "Minimum Trading Days", value: selectedChallenge.min_trading_days ? `${selectedChallenge.min_trading_days} Days` : "N/A"},
      {label: "Maximum Daily Loss", value: selectedChallenge.max_daily_loss ? `${parseFloat(selectedChallenge.max_daily_loss).toFixed(0)}%` : "N/A"},
      {
        label: selectedStepType === "2_step" ? "Maximum Loss" : "Maximum Loss (Trailing)",
        value: selectedChallenge.max_loss ? `${parseFloat(selectedChallenge.max_loss).toFixed(0)}%` : "N/A",
      },
      {
        label: "Profit Target",
        isProfitTarget: true,
      },
      {label: "Platforms", isPlatforms: true},
      {label: "Leverage", value: selectedChallenge.leverage ? `Up to 1:${selectedChallenge.leverage}` : "N/A"},
    ];
  }, [selectedChallenge, selectedStepType]);

  const handleBuyNow = (challengeToBuy = null) => {
    const challenge = challengeToBuy || selectedChallenge;
    if (!challenge) return;

    // Check for restrictions
    if ((currentPlan?.id === 23 || currentPlan?.id === 24) && (userCountry === "Ethiopia" || userCountry === "ET")) {
      return;
    }

    dispatch(setSelectedChallenge(challenge.id));
    // Default to MT5 if no broker selected, since platforms are hardcoded in display
    const defaultBroker = {value: "mt5", label: "MT5"};
    navigate(`/funding-evaluation/payments/${challenge.id}`, {state: defaultBroker});
  };

  if (plansRedux.loading) return <LoaderOverlay />;

  return (
    <div className="new-plans-page">
      <div className="plans-selection-container">
        {/* Plan Type Selector */}
        <div className="selector-group plan-types">
          {planNames.map((name) => (
            <div
              key={name}
              className={`selector-item ${selectedPlanName === name ? "active" : ""}`}
              onClick={() => setSelectedPlanName(name)}
            >
              {planBadges[name] && <span className="badge">{planBadges[name]}</span>}
              <span className="label">{name}</span>
            </div>
          ))}
        </div>

        {/* Step and Platform Selectors Row */}
        {selectedPlanName !== "Instant" && (
          <div className="flex-row">
            {stepTypes.length > 0 && (
              <div className="selector-group step-types">
                {stepTypes.map((type) => (
                  <div
                    key={type}
                    className={`selector-item ${selectedStepType === type ? "active" : ""}`}
                    onClick={() => setSelectedStepType(type)}
                  >
                    <span className="label">{stepLabel(type)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Account Size Selector */}
        <div className="selector-group account-sizes">
          {challenges.map((challenge, index) => (
            <div
              key={challenge.id}
              className={`selector-item ${selectedChallengeId === challenge.id ? "active" : ""}`}
              onClick={() => setSelectedChallengeId(challenge.id)}
            >
              <span className="label">{`${(challenge.starting_balance / 1000).toFixed(challenge.starting_balance % 1000 === 0 ? 0 : 1)}K`}</span>
            </div>
          ))}
        </div>
      </div>

      {isMobile ? (
        <MobilePlans
          challenges={challenges}
          selectedChallengeId={selectedChallengeId}
          setSelectedChallengeId={setSelectedChallengeId}
          handleBuyNow={handleBuyNow}
          selectedStepType={selectedStepType}
          selectedPlanName={selectedPlanName}
        />
      ) : (
        <>
          {/* Comparison Table Section */}
          <div className="comparison-table-wrapper">
            <div className="comparison-table">
              <div className="table-header">
                <div className="header-space"></div>
                <div className="header-content">
                  {phases.map((phase) => (
                    <div
                      key={phase}
                      className={`column-header ${phase === "Funded" ? "highlight" : ""}`}
                    >
                      {phase}
                    </div>
                  ))}
                </div>
              </div>

              <div className="table-body">
                <div className="titles-box">
                  {tableRows.map((row, index) => (
                    <div
                      key={index}
                      className="title-item"
                    >
                      {row.label}
                    </div>
                  ))}
                </div>

                <div className="values-grid">
                  {tableRows.map((row, rowIndex) => (
                    <div
                      key={rowIndex}
                      className={`value-row ${row.isPlatforms ? "platform-row" : ""}`}
                    >
                      {phases.map((phase) => (
                        <div
                          key={phase}
                          className={`column-value ${row.isPlatforms ? "platforms" : ""}`}
                        >
                          {row.isProfitTarget ? (
                            phase === "Phase 1" ? (
                              selectedChallenge?.profit_target_1 ? (
                                `${parseFloat(selectedChallenge.profit_target_1).toFixed(0)}%`
                              ) : (
                                "N/A"
                              )
                            ) : phase === "Phase 2" ? (
                              selectedChallenge?.profit_target_2 ? (
                                `${parseFloat(selectedChallenge.profit_target_2).toFixed(0)}%`
                              ) : (
                                "N/A"
                              )
                            ) : (
                              "-"
                            )
                          ) : row.isPlatforms ? (
                            <>
                              <div className="platform-item">
                                <img
                                  src={mt5}
                                  alt="mt5"
                                />{" "}
                                MetaTrader 5
                              </div>
                              <div className="platform-item">
                                <img
                                  src={matchTrader}
                                  alt="matchTrader"
                                />{" "}
                                Match-Trader
                              </div>
                            </>
                          ) : (
                            row.value
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="buy-button-container">
            <button
              className="buy-button"
              onClick={() => handleBuyNow()}
            >
              <span className="btn-text">
                Buy Now – ${Number(selectedChallenge?.price || 0).toFixed(0)}
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NewPlans;
